import { useEffect, useRef, useState } from 'react'

import {
  IconCircleXmark,
  IconCircleXmarkSolid,
  IconVerticalDivider,
  ModalDialog
} from '@swaggerhub-portal/component-library'
import { Trans, useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'

import { SearchProductsDesktop } from './SearchProducts/desktop'
import { SearchResults } from './SearchResults'
import appDefaults from '~/app.defaults.json'
import { SEARCH_URL } from '~/components/SearchModal/constants'
import { buildSearchParams } from '~/components/SearchModal/helpers'
import { SearchLoaderData } from '~/routes/services.api.search/_index'
import { LandingProduct } from '~/types/product'
import { SEARCH_TAB_TYPE } from '~/types/search'
import { addSearchParams } from '~/utils/shared/routing'

interface ExitBarProps {
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClose: () => void;
}

const ExitBar = ({ isHovered, onMouseEnter, onMouseLeave, onClose }: ExitBarProps) => (
  <div className="flex items-center justify-end gap-x-2">
    <span className="font-medium text-body3 text-neutral-70 dark:text-neutral-40">
      <Trans i18nKey="components.ui.searchModal.cancel" />
    </span>
    <span className="text-neutral-30 dark:text-neutral-80">
      <IconVerticalDivider />
    </span>
    <div
      className="cursor-pointer"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClose}
    >
      {isHovered ? <IconCircleXmarkSolid /> : <IconCircleXmark />}
    </div>
  </div>
)

interface SearchModalDesktopProps {
  isOpen: boolean;
  onClose: () => void;
  accentColor?: string;
  products?: Array<LandingProduct>,
  currentProduct?: LandingProduct,
}

export const SearchModalDesktop = ({ isOpen, onClose, accentColor, products = [], currentProduct }: SearchModalDesktopProps) => {
  const fetcher = useFetcher<SearchLoaderData>()

  const { t } = useTranslation()

  const [searchData, setSearchData] = useState<SearchLoaderData>({
    results: [],
    noOfElements: {},
  })
  const [searchParams, setSearchParams] = useState({})
  const [isHovered, setIsHovered] = useState(false)
  const [inputLength, setInputLength] = useState(0)
  const [tab, setTab] = useState<SEARCH_TAB_TYPE>(SEARCH_TAB_TYPE.ALL)

  const isLoadingRef = useRef(false)

  const { minQueryLength } = appDefaults.search
  
  useEffect(() => {
    if (fetcher.data) {
      setSearchData(fetcher.data)
      isLoadingRef.current = false
    }
  }, [fetcher.data])
  
  const handleSearch = (query: string, type: SEARCH_TAB_TYPE, productId?: string) => {
    if (query?.trim().length < minQueryLength) {
      setSearchData({ results: [], noOfElements: {} })
    } else {
      isLoadingRef.current = true

      const searchParams = buildSearchParams(query, type, productId)
      setSearchParams(searchParams)
      fetcher.load(addSearchParams(SEARCH_URL, searchParams))
    }
  }
  
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      customWidth={696}
      className="h-[38.5rem]"
    >
      <ExitBar
        isHovered={isHovered}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClose={onClose}
      />
      <div>
        <SearchProductsDesktop
          tab={tab}
          onSearch={handleSearch}
          accentColor={accentColor}
          isLoading={fetcher.state === 'loading'}
          onInputLengthChange={setInputLength}
          products={products}
          currentProduct={currentProduct}
          allProductsLabel={t('components.ui.searchModal.allProducts')}
        />
      </div>
      <SearchResults
        noOfElements={searchData.noOfElements}
        results={searchData.results || []}
        isLoading={isLoadingRef.current}
        inputLength={inputLength}
        minQueryLength={minQueryLength}
        onClose={onClose}
        onTabChange={setTab}
        searchParams={searchParams}
        totalElements={searchData.searchPage?.totalElements || 0}
      />
    </ModalDialog>
  )
}